import React from 'react';
import { type MealPlanTemplateSummary } from '@/lib/api/mealPlans';
import trashBinTrashIcon from '@/assets/images/trash-bin-trash.svg';
import { formatNutritionApiRaw } from '@/utils/nutrition-display-format';
import { Tag } from '@/components/ui/tag';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';
import { TagOverflowTooltip } from '@/components/recipes/TagOverflowTooltip';

const getTemplateTitle = (template: MealPlanTemplateSummary): string =>
  template.templateName?.trim() || template.name?.trim() || 'Untitled template';

const getCreatedByDisplayName = (template: MealPlanTemplateSummary): string => {
  const cb = template.createdBy;
  if (!cb || typeof cb !== 'object') return '';
  const full = typeof cb.fullName === 'string' ? cb.fullName.trim() : '';
  const named = typeof cb.name === 'string' ? cb.name.trim() : '';
  return full || named || '';
};

const getTemplateTags = (template: MealPlanTemplateSummary): string[] => {
  const tags = [...(template.clinicalTags ?? []), ...(template.tags ?? [])]
    .map((tag) => formatUnderscoreDisplayLabel(tag))
    .filter(Boolean);
  return Array.from(new Set(tags));
};

const getMetricValue = (template: MealPlanTemplateSummary, keys: string[]): string => {
  const source = template as Record<string, unknown>;
  const totals =
    source.totals && typeof source.totals === 'object'
      ? (source.totals as Record<string, unknown>)
      : null;

  for (const key of keys) {
    const raw = source[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return formatNutritionApiRaw(raw);
    }
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return raw.trim();
    }

    if (totals) {
      const totalsRaw = totals[key];
      if (typeof totalsRaw === 'number' && Number.isFinite(totalsRaw)) {
        return formatNutritionApiRaw(totalsRaw);
      }
      if (typeof totalsRaw === 'string' && totalsRaw.trim().length > 0) {
        return totalsRaw.trim();
      }
    }
  }
  return '--';
};

const getTemplateVisibilityLabel = (template: MealPlanTemplateSummary): string =>
  template.visibility === 'shared_with_practice' ? 'Public' : 'Private';

const TAG_CHIP_GAP_PX = 6;
const TAG_FONT = "500 10px Inter, sans-serif";
const TAG_HORIZONTAL_PADDING_PX = 15;
const TAG_MIN_WIDTH_PX = 44;
const TAG_MAX_WIDTH_PX = 140;

function measureTagChipWidth(text: string): number {
  if (typeof document === 'undefined') return 80;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 80;
  context.font = TAG_FONT;
  const textWidth = Math.ceil(context.measureText(text).width);
  return Math.min(TAG_MAX_WIDTH_PX, Math.max(TAG_MIN_WIDTH_PX, textWidth + TAG_HORIZONTAL_PADDING_PX));
}

type TemplateCardProps = {
  template: MealPlanTemplateSummary;
  onViewTemplate?: (template: MealPlanTemplateSummary) => void;
  onAddTemplate?: (template: MealPlanTemplateSummary) => void;
  onDeleteTemplate?: (template: MealPlanTemplateSummary) => void;
  deleteDisabled?: boolean;
};

/** Meal plan template card — Figma node 174:1974 */
const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onViewTemplate,
  onAddTemplate,
  onDeleteTemplate,
  deleteDisabled = false,
}) => {
  const title = getTemplateTitle(template);
  const createdByName = getCreatedByDisplayName(template);
  const description = template.description?.trim() || 'No description available for this template.';
  const suitableFor = template.suitableFor?.trim();
  const tags = getTemplateTags(template);
  const visibilityLabel = getTemplateVisibilityLabel(template);
  const isSharedWithPractice = template.visibility === 'shared_with_practice';
  const isOwnTemplate = template.isOwnTemplate === true;
  const canEditTemplate = template.canEdit === true || (template.canEdit !== false && isOwnTemplate);
  const canDeleteTemplate = template.canDelete === true || (isOwnTemplate && template.canDelete !== false);
  const tagsWrapRef = React.useRef<HTMLDivElement | null>(null);
  const [availableTagsWidth, setAvailableTagsWidth] = React.useState(0);

  const kcal = getMetricValue(template, ['kcal', 'calories', 'totalCalories']);
  const protein = getMetricValue(template, ['protein', 'totalProtein']);
  const carbs = getMetricValue(template, ['carbs', 'totalCarbs']);
  const fat = getMetricValue(template, ['fat', 'totalFat']);
  const fiber = getMetricValue(template, ['fiber', 'totalFiber']);
  const viewLabel = canEditTemplate
    ? (template.templateKind === 'meal_slot' ? 'Edit Meal Slot' : 'Edit Template')
    : (template.templateKind === 'meal_slot' ? 'View Meal Slot' : 'View Template');
  const tagWidthCache = React.useMemo(() => {
    const cache = new Map<string, number>();
    for (const tag of tags) cache.set(tag, measureTagChipWidth(tag));
    return cache;
  }, [tags]);

  React.useLayoutEffect(() => {
    const node = tagsWrapRef.current;
    if (!node) return;

    const updateWidth = () => {
      setAvailableTagsWidth(node.clientWidth);
    };
    updateWidth();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visibleTagCount = React.useMemo(() => {
    if (!tags.length) return 0;
    if (availableTagsWidth <= 0) return 1;

    let best = 1;
    for (let candidate = 1; candidate <= tags.length; candidate += 1) {
      const hidden = tags.length - candidate;
      let totalWidth = 0;
      for (let i = 0; i < candidate; i += 1) {
        totalWidth += tagWidthCache.get(tags[i]) ?? TAG_MIN_WIDTH_PX;
      }
      if (candidate > 1) totalWidth += TAG_CHIP_GAP_PX * (candidate - 1);
      if (hidden > 0) {
        totalWidth += TAG_CHIP_GAP_PX + measureTagChipWidth(`+${hidden}`);
      }
      if (totalWidth <= availableTagsWidth) {
        best = candidate;
      } else {
        break;
      }
    }
    return best;
  }, [availableTagsWidth, tagWidthCache, tags]);

  const visibleTags = tags.slice(0, visibleTagCount);
  const hiddenTags = tags.slice(visibleTags.length);
  const hiddenTagsCount = hiddenTags.length;
  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDeleteTemplate?.(template);
  };

  return (
    <article
      className="relative flex h-full flex-col items-stretch rounded-[12.876px] border-[1.839px] border-[#E5E7EB] bg-white px-[12.876px] py-[16.554px]"
      style={{ gap: '21.153px' }}
    >
      <div className="absolute right-3 top-4 flex items-center gap-3">
        <span
          className={`inline-flex min-h-[22px] max-w-[180px] items-center justify-center rounded-lg border-0 px-3 py-1 text-center text-xs font-bold leading-[13px] text-white ${
            isOwnTemplate ? 'bg-[#00C28C]' : isSharedWithPractice ? 'bg-[#20AEDC]' : 'bg-[#00C28C]'
          }`}
          title={visibilityLabel}
        >
          {visibilityLabel}
        </span>
        {onDeleteTemplate && canDeleteTemplate ? (
          <button
            type="button"
            disabled={deleteDisabled}
            className="inline-flex h-6 w-6 items-center justify-center text-[#F87171] transition-colors hover:text-[#DC2626] disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleDeleteClick}
            aria-label={`Delete template ${title}`}
          >
            <img src={trashBinTrashIcon} alt="" className="block h-6 w-6" />
          </button>
        ) : null}
      </div>

      <div className="w-full min-w-0 shrink-0 pr-8">
        <h2 className="font-[Inter,sans-serif] text-[14px] font-medium leading-[145%] tracking-[-0.29px] text-[#1E2939] line-clamp-3">
          {title}
        </h2>
        {createdByName ? (
          <p className="mt-1 font-[Inter,sans-serif] text-[12px] font-normal leading-[14.72px] tracking-[0px] text-[#6B7280]">
            <span className="text-[#4A5565]">Created by</span>
            {` ${createdByName}`}
          </p>
        ) : null}
      </div>

      <div className="flex w-full min-w-0 flex-col">
        <div className="w-full pt-[8px] pb-[12px]">
          <p className="font-[Inter,sans-serif] text-[12px] font-normal leading-[14.72px] tracking-[0px] text-[#4A5565]">
            {description}
          </p>
          {suitableFor ? (
            <p className="mt-2 font-[Inter,sans-serif] text-[12px] font-normal leading-[14.72px] tracking-[0px] text-[#4A5565]">
              <span className="font-medium text-[#1E2939]">Suitable for:</span>
              {` ${suitableFor}`}
            </p>
          ) : null}
        </div>

        <div className="flex w-full items-center gap-2 border-b-[0.92px] border-solid border-[rgba(227,227,227,0.41)] py-[11.036px]">
          <div className="inline-flex shrink-0 items-center rounded-full bg-[#EBF9FE] px-2.5 py-1">
            <span className="whitespace-nowrap font-[Inter,sans-serif] text-[18px] font-bold leading-[25.751px] tracking-[-0.4042px] text-[#0891B2]">
              {kcal} kcal
            </span>
          </div>
          <div
            ref={tagsWrapRef}
            className="flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-[5.518px]"
          >
            {visibleTags.map((tag) => (
              <Tag
                key={tag}
                variant="info"
                size="sm"
                maxWidth={140}
                className="h-[25.751px] px-[7.358px] py-[2.759px] font-[Inter,sans-serif] font-medium leading-[13.795px] text-[#0A0A0A]"
                title={tag}
              >
                {tag}
              </Tag>
            ))}
            {hiddenTagsCount > 0 ? (
              <TagOverflowTooltip
                label="tags"
                values={hiddenTags}
                triggerLabel={`+${hiddenTagsCount}`}
                className="h-[25.751px] px-[7.358px] py-[2.759px] font-[Inter,sans-serif] leading-[13.795px]"
                contentClassName="text-[#0A0A0A]"
              />
            ) : null}
          </div>
        </div>

        <div className="flex w-full items-start justify-between border-b-[0.92px] border-solid border-[rgba(227,227,227,0.41)] pt-[11.036px] pb-[11.956px] font-[Inter,sans-serif] text-[12px] font-normal leading-[14.715px] whitespace-nowrap text-[#4A5565]">
          <span>
            Protein <strong className="font-bold">{protein}g</strong>
          </span>
          <span>
            Carbs <strong className="font-bold">{carbs}g</strong>
          </span>
          <span>
            Fat <strong className="font-bold">{fat}g</strong>
          </span>
          <span>
            Fiber <strong className="font-bold">{fiber}g</strong>
          </span>
        </div>
      </div>

      <div className="mt-auto grid h-10 w-full grid-cols-2 gap-[7.358px]">
        <button
          type="button"
          className={`rounded-[9.125px] bg-[#20AEDC] px-3 font-[Inter,sans-serif] text-[14px] font-medium leading-[18.249px] tracking-[-0.1372px] text-white${onAddTemplate ? '' : ' col-span-2'}`}
          onClick={() => onViewTemplate?.(template)}
        >
          {viewLabel}
        </button>
        {onAddTemplate ? (
          <button
            type="button"
            className="rounded-[9.125px] border-[0.912px] border-solid border-[#D1D5DC] bg-white px-3 font-[Inter,sans-serif] text-[14px] font-medium leading-[18.249px] tracking-[-0.1372px] text-[#6A7282]"
            onClick={() => onAddTemplate(template)}
          >
            Add to Meal Plan
          </button>
        ) : null}
      </div>
    </article>
  );
};

export default TemplateCard;
